import React, { useEffect, useState } from 'react'

import {
  Button,
  EmptyState,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CellChip } from 'features/table-columns'
import { AntecipationItem, AntecipationBilling } from 'models/Receivables'
import moment from 'moment'
import { rgba } from 'polished'
import { ReceivablesService } from 'services/ReceivablesService'

import { ButtonRounded } from 'components/button'
import IconContainer from 'components/IconContainer/IconContainer'
import { Table } from 'components/Table/Table'
import { HeadCell } from 'components/Table/Table.interface'
import { TableSkeleton } from 'components/Table/TableSkeleton'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import {
  DETAIL_MODAL_HEIGHT,
  DETAIL_MODAL_MAX_WIDTH
} from '../../ModalBalance.constants'
import * as S from '../../ModalBalance.styles'

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  credit_card: 'Cartão de crédito',
  credit_card_recurring: 'Cartão recorrente',
  credit_card_installments: 'Cartão parcelado',
  boleto: 'Boleto',
  pix: 'Pix'
}

type StatusStyle = { label: string; color: string; background: string }

const STATUS_MAP: Record<string, StatusStyle> = {
  pending: {
    label: 'Em processamento',
    color: theme.palette.neutral[400],
    background: rgba(theme.palette.neutral[700], 0.3)
  },
  requested: {
    label: 'Em processamento',
    color: theme.palette.secondary[200],
    background: rgba(theme.palette.secondary[200], 0.2)
  },
  approved: {
    label: 'Aprovada',
    color: theme.palette.primary[200],
    background: rgba(theme.palette.primary[200], 0.2)
  },
  completed: {
    label: 'Concluída',
    color: theme.palette.primary[200],
    background: rgba(theme.palette.primary[200], 0.2)
  },
  canceled: {
    label: 'Cancelada',
    color: theme.palette.error[300],
    background: rgba(theme.palette.error[300], 0.1)
  },
  rejected: {
    label: 'Rejeitada',
    color: theme.palette.error[300],
    background: rgba(theme.palette.error[300], 0.1)
  }
}

const HEADER: HeadCell[] = [
  { id: 'status', label: 'Status' },
  { id: 'date', label: 'Data' },
  { id: 'details', label: 'Detalhes' },
  { id: 'actions', label: '' }
]

const BILLING_HEADER: HeadCell[] = [
  { id: 'customer', label: 'Cliente' },
  { id: 'date_paid', label: 'Data Pagamento' },
  { id: 'due_date', label: 'Data Vencimento' },
  { id: 'gross_amount', label: 'Valor Bruto' },
  { id: 'fee', label: 'Tarifa' },
  { id: 'amount', label: 'Valor Líquido' },
  { id: 'payment_type', label: 'Método' },
  { id: 'actions', label: '' }
]

const TECHNICAL_MESSAGES: Record<string, string> = {
  'Antecipação inválida. Parâmetros inválidos ou não há limite de crédito disponível!':
    'Antecipação negada, tente novamente.'
}

interface AntecipationsListProps {
  active: boolean
  onOpenBilling: (uuid: string) => void
}

export const AntecipationsList = ({
  active,
  onOpenBilling
}: AntecipationsListProps) => {
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState<AntecipationItem[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedAntecipation, setSelectedAntecipation] =
    useState<AntecipationItem | null>(null)
  const [billingPage, setBillingPage] = useState(0)
  const [billingRowsPerPage, setBillingRowsPerPage] = useState(25)

  useEffect(() => {
    if (!active) return

    const fetch = async () => {
      setLoading(true)
      const data = await ReceivablesService.getAntecipations(
        page + 1,
        rowsPerPage
      )
      setRecords(data.records)
      setTotalCount(data.meta.total_count)
      setLoading(false)
    }

    fetch()
  }, [active, page, rowsPerPage])

  useEffect(() => {
    setBillingPage(0)
  }, [selectedAntecipation])

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number(e.target.value || 25))
    setPage(0)
  }

  const handleBillingChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setBillingPage(newPage)
  }

  const handleBillingChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setBillingRowsPerPage(Number(e.target.value || 25))
    setBillingPage(0)
  }

  const getStatusDetails = (item: AntecipationItem) => {
    if (!item.status_details) return '-'
    return TECHNICAL_MESSAGES[item.status_details] ?? item.status_details
  }

  const buildBillingRows = (billings: AntecipationBilling[]) => {
    const filtered = billings.filter((b) => b.billing_uuid)
    const paginated = filtered.slice(
      billingPage * billingRowsPerPage,
      (billingPage + 1) * billingRowsPerPage
    )

    return paginated.map((b) => {
      const fee = Number(b.gross_amount) - Number(b.amount)

      return {
        uuid: b.billing_uuid as string,
        clickable: true,
        customer: (
          <Typography variant="smLight">{b.customer_name || '-'}</Typography>
        ),
        date_paid: b.date_paid ? moment(b.date_paid).format('DD/MM/YYYY') : '-',
        due_date: b.due_date ? moment(b.due_date).format('DD/MM/YYYY') : '-',
        gross_amount: formatMoney(Number(b.gross_amount)),
        fee: formatMoney(fee),
        amount: formatMoney(Number(b.amount)),
        payment_type: PAYMENT_TYPE_LABEL[b.payment_type] || b.payment_type,
        actions: (
          <Box display="flex" justifyContent="flex-end">
            <Tooltip
              enterTouchDelay={0}
              arrow
              placement="top"
              title="Detalhes da cobrança"
            >
              <Box>
                <ButtonRounded
                  onClick={() => onOpenBilling(b.billing_uuid as string)}
                  variant="transparent"
                >
                  <Icon
                    icon="settings"
                    sx={{ color: theme.palette.neutral[200] }}
                  />
                </ButtonRounded>
              </Box>
            </Tooltip>
          </Box>
        )
      }
    })
  }

  if (loading) {
    return (
      <TableSkeleton
        columns={4}
        rows={5}
        columnWidths={['22%', '24%', '44%', '10%']}
        cellRenderHint={['chip', 'text', 'text', 'icon']}
      />
    )
  }

  if (!loading && !records.length) {
    return (
      <S.EmptyStateWrapper>
        <Box maxWidth="600px" width="100%">
          <EmptyState
            title="Nenhum registro encontrado"
            message="Você ainda não possui antecipações realizadas."
          />
        </Box>
      </S.EmptyStateWrapper>
    )
  }

  const rows = records.map((item) => {
    const statusInfo = STATUS_MAP[item.status] ?? {
      label: 'Desconhecido',
      color: theme.palette.neutral[400],
      background: rgba(theme.palette.neutral[700], 0.3)
    }
    const formattedDate = format(
      new Date(item.created_at),
      "dd/MM/yyyy 'às' HH:mm",
      { locale: ptBR }
    )
    const hasBillings = item.billings?.some((b) => b.billing_uuid)

    return {
      uuid: item.uuid,
      clickable: hasBillings,
      status: (
        <CellChip
          tone="custom"
          color={statusInfo.color}
          background={statusInfo.background}
          label={statusInfo.label}
        />
      ),
      date: (
        <Typography variant="smLight" color="text.secondary">
          {formattedDate}
        </Typography>
      ),
      details: (
        <Typography variant="smLight" color="text.secondary">
          {getStatusDetails(item)}
        </Typography>
      ),
      actions: (
        <Box display="flex" justifyContent="flex-end">
          <Tooltip
            enterTouchDelay={0}
            arrow
            placement="top"
            PopperProps={{ disablePortal: true }}
            title={hasBillings ? 'Ver faturas antecipadas' : ''}
          >
            <Box>
              <ButtonRounded
                disabled={!hasBillings}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedAntecipation(item)
                }}
                variant="transparent"
              >
                <Icon
                  icon="settings"
                  sx={{
                    color: hasBillings
                      ? theme.palette.neutral[200]
                      : theme.palette.neutral[500]
                  }}
                />
              </ButtonRounded>
            </Box>
          </Tooltip>
        </Box>
      )
    }
  })

  const allBillings =
    selectedAntecipation?.billings?.filter((b) => b.billing_uuid) ?? []

  return (
    <>
      <S.TableWrapper>
        <Table
          mobileCards
          header={HEADER}
          rows={rows}
          disableFilter
          select={{ useSelect: false }}
          hoverInRow
          hideLastRowSeparator
          rowAction={(uuid) => {
            const item = records.find((r) => r.uuid === uuid)
            if (item) setSelectedAntecipation(item)
          }}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </S.TableWrapper>

      <Modal
        open={!!selectedAntecipation}
        handleClose={() => setSelectedAntecipation(null)}
        size="large"
        width="100%"
        maxWidth={DETAIL_MODAL_MAX_WIDTH}
        height={DETAIL_MODAL_HEIGHT}
      >
        <ModalBody
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <ModalHeader onClose={() => setSelectedAntecipation(null)}>
            <Stack direction="row" alignItems="center" gap={2}>
              <IconContainer icon="receipt" />
              <Typography variant="mdBold">Faturas antecipadas</Typography>
            </Stack>
          </ModalHeader>

          <ModalContent sx={{ flex: 1, minHeight: 0 }}>
            <Table
              mobileCards
              header={BILLING_HEADER}
              rows={buildBillingRows(allBillings)}
              disableFilter
              select={{ useSelect: false }}
              hoverInRow
              hideLastRowSeparator
              rowAction={(uuid) => onOpenBilling(uuid)}
              page={billingPage}
              rowsPerPage={billingRowsPerPage}
              totalCount={allBillings.length}
              handleChangePage={handleBillingChangePage}
              handleChangeRowsPerPage={handleBillingChangeRowsPerPage}
            />
          </ModalContent>

          <ModalFooter>
            <Button
              variantButton="neutral"
              onClick={() => setSelectedAntecipation(null)}
            >
              Fechar
            </Button>
          </ModalFooter>
        </ModalBody>
      </Modal>
    </>
  )
}
